import { useEffect } from "react";

import { useQuery } from "@tanstack/react-query";

import { apiPaths } from "@/src/api";

import { userAuctionsBadgesAtom } from "@/src/state/userAuctionsBadgesAtom";

import { useAuth } from "@/src/auth/hooks/useAuth";
import { ConsumerQueryKeys } from "@/src/queries/consumer/consumerFunctions";
import { defaultQueryFn } from "@/src/queries/defaultQuery";
import { AuctionState, ConsumerAuctionDTO } from "@/src/types/types";

import { useSetAtom } from "jotai";

export const useGetAllAuctions = () => {
  const { isAuthenticated, isAuthenticating, isConsumer } = useAuth();
  const setUserAuctionsBadges = useSetAtom(userAuctionsBadgesAtom);

  const query = useQuery({
    queryKey: [ConsumerQueryKeys.AUCTIONS],
    queryFn: () => defaultQueryFn({ queryKey: ["/auctions"] }),
    select: (auctionItems) => {
      return auctionItems as ConsumerAuctionDTO[];
    },
    enabled: isAuthenticated && !isAuthenticating && isConsumer,
    retry: (failureCount, error) => {
      // Retry on network errors (like ERR_NETWORK_CHANGED when laptop lid closes/opens)
      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        return failureCount < 3;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnReconnect: true
  });

  // v5 removed onSuccess from useQuery; derive badge state from `data` instead.
  useEffect(() => {
    if (!query.data) return;
    const activeAuction = query.data.find(
      (auction) =>
        auction.auctionState === AuctionState.STARTED ||
        auction.auctionState === AuctionState.ENDED
    );
    const draftAuction = query.data.find(
      (auction) => auction.auctionState === AuctionState.DRAFT
    );
    setUserAuctionsBadges((prev) => ({
      ...prev,
      draftAuctionId: draftAuction?.id,
      activeAuctionId: activeAuction?.id
    }));
  }, [query.data, setUserAuctionsBadges]);

  return query;
};

export const useGetDraftAuction = () => {
  const { isAuthenticated, isAuthenticating, isConsumer } = useAuth();
  const setUserAuctionsBadges = useSetAtom(userAuctionsBadgesAtom);

  const query = useQuery({
    queryKey: [ConsumerQueryKeys.AUCTIONS + "/draft"],
    queryFn: () => defaultQueryFn({ queryKey: ["/auctions"] }),
    select: (auctions) => {
      const draftAuctions = (auctions as ConsumerAuctionDTO[]).filter(
        (auction) => {
          return auction.auctionState === AuctionState.DRAFT;
        }
      );
      return draftAuctions.length > 0 ? draftAuctions[0] : undefined;
    },
    enabled: isAuthenticated && !isAuthenticating && isConsumer
  });

  // v5 removed onSuccess from useQuery; derive badge state from query result
  // instead. Gate on isSuccess (not data truthiness) so the badge also clears
  // when a draft disappears, and use the functional updater to avoid wiping
  // activeAuctionId that useGetAllAuctions may have written concurrently.
  useEffect(() => {
    if (!query.isSuccess) return;
    setUserAuctionsBadges((prev) => ({
      ...prev,
      draftAuctionId: query.data?.id
    }));
  }, [query.isSuccess, query.data, setUserAuctionsBadges]);

  return query;
};

export const useGetConsumerAuction = (auctionId?: number | string) => {
  const { isAuthenticated, isAuthenticating, isConsumer } = useAuth();

  return useQuery({
    queryKey: [`${ConsumerQueryKeys.AUCTIONS}/${auctionId}`],
    queryFn: () =>
      defaultQueryFn({ queryKey: [`${apiPaths.AUCTIONS}/${auctionId}`] }),
    select: (auctionItem) => {
      return auctionItem as ConsumerAuctionDTO;
    },
    enabled:
      isAuthenticated &&
      !isAuthenticating &&
      isConsumer &&
      auctionId !== undefined &&
      auctionId !== "undefined" &&
      auctionId !== null
  });
};
